import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { Box, List, ListItem, ListItemButton, ListItemText, Paper, Skeleton, Stack, Typography } from '@mui/material'
import { format } from 'date-fns'
import { nb } from 'date-fns/locale'
import { WorkItem, WorkResponse } from '../../../interfaces/IWork'
import { WorkStatus } from '../../../interfaces/IWorkEnums'
import { usePolling } from '../../../hooks/usePolling'
import { blue } from '@mui/material/colors'
import InfoTooltip from './InfoTooltip'

interface ActiveWorkCardProps {
  variant?: 'compact' | 'wall'
  info?: string
}

function ActiveWorkCard({ variant = 'compact', info }: ActiveWorkCardProps) {
  const navigate = useNavigate()
  const isWall = variant === 'wall'
  const { data } = usePolling<WorkResponse>('/api/work', 5 * 60_000)

  const activeWork = useMemo<WorkItem[]>(() => {
    if (!data?.entities) return []
    return data.entities
      .filter((w) => w.status === ('InProgress' as unknown as WorkStatus))
      .sort((a, b) => new Date(b.startedDate).getTime() - new Date(a.startedDate).getTime())
  }, [data])

  const totalActive = useMemo(() => {
    if (!data?.entities) return 0
    return data.entities.filter((w) => w.status === ('InProgress' as unknown as WorkStatus)).length
  }, [data])

  let body
  if (!data) {
    body = (
      <List dense disablePadding>
        {Array.from({ length: 3 }).map((_, i) => (
          <ListItem key={i} sx={{ borderLeft: '4px solid', borderColor: 'divider', mb: 0, py: 0.5 }}>
            <ListItemText
              primary={<Skeleton variant="text" width="60%" />}
              secondary={<Skeleton variant="text" width="40%" />}
            />
          </ListItem>
        ))}
      </List>
    )
  } else if (activeWork.length === 0) {
    body = <Typography variant="body2" color="text.secondary">Ingen pågående arbeid</Typography>
  } else {
    body = (
      <Box sx={{ overflowY: 'auto', flex: 1, minHeight: 0 }}>
        <List dense disablePadding>
          {activeWork.map((w) => {
            const hasEventLink = !!w.externalReferenceId
            const startedLabel = format(new Date(w.startedDate), 'dd.MM HH:mm', { locale: nb })
            const location = w.assetLocation ?? 'Ukjent lokasjon'
            const content = (
              <ListItemText
                sx={{ my: 0 }}
                slotProps={{ secondary: { variant: 'caption' } }}
                primary={
                  <Stack direction="row" spacing={1} sx={{ alignItems: 'baseline' }}>
                    <Typography variant="body2" component="span" sx={{ fontWeight: 600 }} noWrap>
                      {location}
                    </Typography>
                    {hasEventLink && (
                      <Typography component="span" variant="caption" color="text.secondary">
                        → {w.externalReferenceId}
                      </Typography>
                    )}
                  </Stack>
                }
                secondary={`Startet ${startedLabel} · ${w.workerPhoneNumber ?? 'Ingen telefon'}`}
              />
            )
            if (hasEventLink) {
              return (
                <ListItem key={w.id} disablePadding sx={{ borderLeft: `4px solid ${blue[400]}`, mb: 0 }}>
                  <ListItemButton onClick={() => navigate(`/events/${w.externalReferenceId}`)} sx={{ py: 0.25 }}>
                    {content}
                  </ListItemButton>
                </ListItem>
              )
            }
            return (
              <ListItem key={w.id} sx={{ borderLeft: `4px solid ${blue[400]}`, mb: 0, py: 0.5 }}>
                {content}
              </ListItem>
            )
          })}
        </List>
      </Box>
    )
  }

  return (
    <Paper
      elevation={1}
      sx={{
        p: isWall ? { xs: 2, md: 3 } : 2,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        maxHeight: isWall ? '45vh' : 480,
        minHeight: 0,
      }}
    >
      <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'baseline', mb: 1, flexShrink: 0 }}>
        <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
          <Typography
            variant={isWall ? undefined : 'h6'}
            sx={{ fontSize: isWall ? 'clamp(1.25rem, 2vw, 2.25rem)' : undefined, fontWeight: 600 }}
          >
            Pågående arbeid
          </Typography>
          {info && !isWall && <InfoTooltip text={info} />}
        </Stack>
        <Typography
          sx={{ fontSize: isWall ? 'clamp(1.5rem, 2.5vw, 2.75rem)' : undefined, fontWeight: 700 }}
          variant={isWall ? undefined : 'h6'}
          color={totalActive > 0 ? undefined : 'text.secondary'}
        >
          {data ? totalActive : '—'}
        </Typography>
      </Stack>
      {body}
    </Paper>
  )
}

export default ActiveWorkCard
